// src/api/imageApi.ts
// API client for mobile-trigger-detector to connect with lens-server backend
// Based on RESTful API v2.0 as defined in openapi.yaml

const API_BASE = "https://g5rq413oej.execute-api.eu-west-2.amazonaws.com";

const getUserId = (): string => {
  // For mobile-trigger-detector, we'll use sessionKey from localStorage as user_id
  // First try sessionKey (for login verification), then userId (for normal operation)
  const sessionKey = localStorage.getItem('sessionKey') || localStorage.getItem('userId');
  if (!sessionKey || sessionKey.trim() === '') {
    throw new Error('Session key not found or empty. Please log in first.');
  }
  return sessionKey.trim();
};

// Type definitions based on openapi.yaml and mobile-trigger-detector requirements
export interface Trigger {
  trigger_id: string;
  image_id: string;
  explanation: string;
  title: string;
  type: string;
  updated_at: string;
  // Additional properties for mobile-trigger-detector compatibility
  id?: string; // For backward compatibility
  confidence?: number; // For trigger confidence scores
  isAccepted?: boolean; // For user acceptance status
  rating?: number | null; // For user ratings
}

export interface Image {
  image_id: string;
  upload_date: string;
  s3_location: string;
  metadata?: object;
  analysis_text?: string;
  triggers?: Trigger[]; // 直接关联的triggers
  triggers_list?: any[]; // API文档中提到的triggers_list
  analysis?: {
    keyword?: string;
    triggers?: Trigger[]; // 解析后的分析数据中的triggers
  };
  keyword?: string;
  source?: 'parsed' | 'raw' | 'invalid';
  trigger_ids?: string[];
  url?: string; // Presigned URL for accessing the image
  emo_intensity_before?: number; // 焦虑程度 (0-10)
  emo_intensity_after?: number; // 分析后的焦虑程度 (0-10)
  relatability_after?: number; // 相关性评分 (0-10)
  helpfulness_after?: number; // 有用性评分 (0-10)
  learnability_after?: number; // 可学习性评分 (0-10)
  comment_after?: string; // 用户评论
  best_suggestion?: string; // 最佳建议类型
  suggestions?: {
    cognitive_reappraisal?: string;
    problem_focus_coping?: string;
    media_regulation?: string;
  };
  suggestion_ratings?: {
    cognitive_reappraisal?: number;
    problem_focus_coping?: number;
    media_regulation?: number;
  };
}

export interface AnalysisData {
  keyword: string;
  triggers: Trigger[];
  rawAnalysisResult?: string;
  imageFileName?: string;
  userIdentity?: {
    gender: string;
    ageGroup: string;
    occupation: string;
    otherInfo: string;
  };
}

export interface ImageResponse {
  message: string;
  image_id: string;
  file_key: string;
  remaining_quota: number;
}

export interface FetchImagesResponse {
  images: Image[];
  next_token?: string;
  has_more: boolean;
}

export interface TriggerUpdate {
  image_id: string;
  explanation?: string;
  title?: string;
}

export interface TriggerDelete {
  image_id: string;
  is_reported?: boolean;
  report_reason?: string;
}

export interface CustomTriggerRequest {
  image_id: string;
  title: string;
  explanation: string;
  type?: string;
}

export interface KeywordUpdate {
  keyword: string;
}

export interface ApiError {
  message: string;
  error_code?: string;
}

// User Information interfaces based on openapi.yaml
export interface UserInfo {
  user_id: string;
  created_at: number;
  status: 'new' | 'active' | 'inactive';
  quota: number;
  gender?: string;
  age_range?: string;
  occupation?: string;
  language: string;
  mental_health_history?: string;
  other_info?: string;
  interview_contact?: string;
  raffle_contact?: string;
  gad7_score?: number;
  gad7_updated_at?: number;
}

export interface UserInfoRequest {
  gender?: string;
  age_range?: string;
  occupation?: string;
  language: string;
  mental_health_history?: string;
  other_info?: string;
}

export interface UserInfoResponse {
  message: string;
  user_id: string;
  status: string;
}

export interface UserGad7Request {
  gad7_score: number;
}

export interface UserGad7Response {
  message: string;
  user_id: string;
  gad7_score: number;
  updated_at: number;
}

export interface UserContactRequest {
  interview_contact?: string;
  raffle_contact?: string;
}

export interface UserContactResponse {
  message: string;
  user_id: string;
  contact_types: string[];
}

export interface NewUserResponse {
  message: string;
  user_id: string;
  created_at: number;
  status: 'new' | 'active' | 'inactive';
  quota: number;
}

export interface UserLoginResponse {
  message: string;
  status: 'activated' | 'existing';
  user_info: {
    created_at: number;
    status: 'new' | 'active' | 'inactive';
    quota: number;
    activated_at?: number;
    gender?: string;
    age_range?: string;
    occupation?: string;
    language?: string;
    mental_health_history?: string;
    other_info?: string;
    interview_contact?: string;
    raffle_contact?: string;
    gad7_score?: number;
    gad7_updated_at?: number;
  };
}

/**
 * Request body for generating AI suggestions
 */
export interface SuggestionRequest {
  type: 'cognitive_reappraisal' | 'problem_focus_coping' | 'media_regulation';
}

/**
 * Response from AI suggestion generation
 */
export interface SuggestionResponse {
  suggestion: string;
  type: 'cognitive_reappraisal' | 'problem_focus_coping' | 'media_regulation';
  from_cache: boolean;
}

export interface SuggestionReportRequest {
  type: 'cognitive_reappraisal' | 'problem_focus_coping' | 'media_regulation';
  report_content: string;
}

export interface SuggestionReportResponse {
  message: string;
  type: 'cognitive_reappraisal' | 'problem_focus_coping' | 'media_regulation';
  image_id: string;
  reported_at: number;
}

export interface SuggestionRatingRequest {
  type: 'cognitive_reappraisal' | 'problem_focus_coping' | 'media_regulation';
  rating: number;
}

export interface SuggestionRatingResponse {
  message: string;
  type: 'cognitive_reappraisal' | 'problem_focus_coping' | 'media_regulation';
  rating: number;
  image_id: string;
  rated_at: number;
}

// Helper function to handle API errors
const handleApiError = async (response: Response): Promise<never> => {
  let errorMessage = `API Error: ${response.status} ${response.statusText}`;
  
  try {
    const errorData: ApiError = await response.json();
    errorMessage = errorData.message || errorMessage;
  } catch {
    // If JSON parsing fails, use the default error message
  }
  
  throw new Error(errorMessage);
};

// Helper function to convert File/base64 to the format expected by the backend
const prepareImageData = async (imageData: File | string): Promise<{ body: ArrayBuffer | string; contentType: string }> => {
  if (imageData instanceof File) {
    return {
      body: await imageData.arrayBuffer(),
      contentType: imageData.type
    };
  } else if (typeof imageData === 'string' && imageData.startsWith('data:')) {
    // Handle base64 data URL
    const [header, base64Data] = imageData.split(',');
    const mimeMatch = header.match(/data:([^;]+)/);
    const contentType = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    
    // Convert base64 to ArrayBuffer
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    return {
      body: bytes.buffer,
      contentType
    };
  } else {
    throw new Error('Unsupported image data format');
  }
};

// ==================== IMAGES API v2.0 ====================

/**
 * Upload an image to the backend server
 * @param imageData - File object or base64 data URL
 * @param keyword - Optional keyword to associate with the image
 * @returns Promise<ImageResponse>
 */
export async function uploadImage(imageData: File | string, keyword?: string): Promise<ImageResponse> {
  const { body, contentType } = await prepareImageData(imageData);
  
  const res = await fetch(`${API_BASE}/2.0/images`, {
    method: 'POST',
    headers: {
      'Content-Type': contentType,
      'user_id': getUserId()
    },
    body: body,
  });
  
  if (!res.ok) await handleApiError(res);
  
  const response = await res.json();
  
  // If keyword is provided, update it after successful upload
  if (keyword && response.image_id) {
    try {
      await updateImageKeyword(response.image_id, keyword);
    } catch (error) {
      console.warn('Failed to update keyword after image upload:', error);
    }
  }
  
  return response;
}

/**
 * Get a single image by ID
 * @param imageId - Image ID
 * @param includeUrl - Whether to include presigned URL
 * @returns Promise<Image>
 */
export async function fetchImage(imageId: string, includeUrl: boolean = true): Promise<Image> {
  const queryParams = new URLSearchParams({
    include_url: includeUrl.toString()
  });
  
  const res = await fetch(`${API_BASE}/2.0/images/${encodeURIComponent(imageId)}?${queryParams.toString()}`, {
    headers: {
      'user_id': getUserId()
    }
  });
  
  if (!res.ok) await handleApiError(res);
  
  return res.json();
}

/**
 * Get a single image by ID with all details
 * @param imageId - Image ID
 * @param includeUrl - Whether to include presigned URL
 * @returns Promise<Image>
 */
export async function fetchImageDetail(imageId: string, includeUrl: boolean = true): Promise<Image> {
  const queryParams = new URLSearchParams({
    include_url: includeUrl.toString(),
    include_analysis: 'true', // 获取分析数据，包括triggers和keyword
    include_suggestions: 'true', // 获取建议数据
    include_feel: 'true', // 获取情感数据
    include_all: 'true' // 获取所有可用字段
  });
  
  const res = await fetch(`${API_BASE}/2.0/images/${encodeURIComponent(imageId)}?${queryParams.toString()}`, {
    headers: {
      'user_id': getUserId()
    }
  });
  
  if (!res.ok) await handleApiError(res);
  
  return res.json();
}

/**
 * Get all images for the current user
 * @param includeUrls - Whether to include presigned URLs
 * @returns Promise<{ images: Image[] }>
 */
export async function fetchAllImages(includeUrls: boolean = true): Promise<{ images: Image[] }> {
  try {
    console.log('[fetchAllImages] Starting fetch with includeUrls:', includeUrls);
    const userId = getUserId(); // This will throw if userId is not available
    console.log('[fetchAllImages] User ID:', userId);
    
    const queryParams = new URLSearchParams({
      include_urls: includeUrls.toString(),
      include_all: 'true' // 获取所有信息，包括分析数据、建议数据、情感数据等
    });
    
    const url = `${API_BASE}/2.0/images/all?${queryParams.toString()}`;
    console.log('[fetchAllImages] Request URL:', url);
    console.log('[fetchAllImages] Query parameters:', queryParams.toString());
    
    const res = await fetch(url, {
      headers: {
        'user_id': userId
      }
    });
    
    console.log('[fetchAllImages] Response status:', res.status);
    console.log('[fetchAllImages] Response ok:', res.ok);
    
    if (!res.ok) {
      console.error('[fetchAllImages] Response not ok, calling handleApiError');
      await handleApiError(res);
    }
    
    const data = await res.json();
    console.log('[fetchAllImages] Response data structure:', {
      hasImages: !!data.images,
      imagesCount: data.images ? data.images.length : 0,
      dataKeys: Object.keys(data)
    });
    
    if (data.images && data.images.length > 0) {
      console.log('[fetchAllImages] First image sample:', {
        image_id: data.images[0].image_id,
        keyword: data.images[0].keyword,
        hasAnalysis: !!data.images[0].analysis,
        hasTriggers: !!data.images[0].triggers,
        hasSuggestions: !!data.images[0].suggestions,
        hasEmoIntensity: !!data.images[0].emo_intensity_before || !!data.images[0].emo_intensity_after,
        hasUserEvaluation: !!(data.images[0].relatability_after || data.images[0].helpfulness_after || data.images[0].learnability_after)
      });
      
      // Log detailed structure of first image
      console.log('[fetchAllImages] Detailed first image structure:', JSON.stringify(data.images[0], null, 2));
    } else {
      console.log('[fetchAllImages] No images found in response');
    }
    
    return data;
  } catch (error) {
    console.error('[fetchAllImages] Error occurred:', error);
    // Only return empty array for authentication errors, re-throw server errors
    if (error instanceof Error && error.message.includes('Session key not found')) {
      console.warn('Cannot fetch images: User not authenticated', error);
      return { images: [] };
    }
    // For server errors, re-throw to let caller handle
    console.error('Failed to fetch all images:', error);
    throw error;
  }
}

/**
 * Get images with pagination
 * @param params - Fetch parameters
 * @returns Promise<FetchImagesResponse>
 */
export async function fetchImages(params: {
  limit?: number;
  nextToken?: string;
  includeUrls?: boolean;
} = {}): Promise<FetchImagesResponse> {
  try {
    const userId = getUserId(); // This will throw if userId is not available
    
    const queryParams = new URLSearchParams({
      include_urls: (params.includeUrls !== false).toString() // Default to true
    });
    
    if (params.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    
    if (params.nextToken) {
      queryParams.append('next_token', params.nextToken);
    }
    
    const res = await fetch(`${API_BASE}/2.0/images?${queryParams.toString()}`, {
      headers: {
        'user_id': userId
      }
    });
    
    if (!res.ok) await handleApiError(res);
    
    return res.json();
  } catch (error) {
    // Only return empty array for authentication errors, re-throw server errors
    if (error instanceof Error && error.message.includes('Session key not found')) {
      console.warn('Cannot fetch images: User not authenticated', error);
      return { 
        images: [], 
        has_more: false 
      };
    }
    // For server errors, re-throw to let caller handle
    console.error('Failed to fetch images:', error);
    throw error;
  }
}

/**
 * Delete an image
 * @param imageId - Image ID to delete
 * @returns Promise<void>
 */
export async function deleteImage(imageId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/2.0/images/${encodeURIComponent(imageId)}`, {
    method: 'DELETE',
    headers: {
      'user_id': getUserId()
    }
  });
  
  if (!res.ok) await handleApiError(res);
}

/**
 * Update image keyword
 * @param imageId - Image ID
 * @param keyword - New keyword
 * @returns Promise<Image>
 */
export async function updateImageKeyword(imageId: string, keyword: string): Promise<Image> {
  const updateData: KeywordUpdate = { keyword };
  
  const res = await fetch(`${API_BASE}/2.0/images/${encodeURIComponent(imageId)}/keyword`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'user_id': getUserId()
    },
    body: JSON.stringify(updateData)
  });
  
  if (!res.ok) await handleApiError(res);
  
  return res.json();
}

// ==================== TRIGGERS API v2.0 ====================

/**
 * Get triggers for a specific image
 * @param imageId - Image ID
 * @returns Promise<{ triggers: Trigger[]; count: number; image_id: string }>
 */
export async function fetchTriggers(imageId: string): Promise<{ triggers: Trigger[]; count: number; image_id: string }> {
  const queryParams = new URLSearchParams({
    image_id: imageId
  });
  
  const res = await fetch(`${API_BASE}/2.0/triggers?${queryParams.toString()}`, {
    headers: {
      'user_id': getUserId()
    }
  });
  if (!res.ok) await handleApiError(res);
  
  return res.json();
}

/**
 * Get AI analysis triggers for a specific image
 * @param imageId - Image ID
 * @returns Promise<{ triggers: Trigger[] }>
 */
export async function getImageTriggers(imageId: string): Promise<{ triggers: Trigger[] }> {
  const res = await fetch(`${API_BASE}/2.0/images/${encodeURIComponent(imageId)}/triggers`, {
    headers: {
      'user_id': getUserId()
    }
  });
  
  if (!res.ok) await handleApiError(res);
  
  return res.json();
}

/**
 * Get latest user-confirmed triggers for a specific image
 * @param imageId - Image ID
 * @returns Promise<{ triggers: Trigger[] }>
 */
export async function getLatestImageTriggers(imageId: string): Promise<{ triggers: Trigger[] }> {
  const res = await fetch(`${API_BASE}/2.0/images/${encodeURIComponent(imageId)}/triggers/latest`, {
    headers: {
      'user_id': getUserId()
    }
  });
  
  if (!res.ok) await handleApiError(res);
  
  return res.json();
}

/**
 * Update a trigger
 * @param triggerId - Trigger ID
 * @param updateData - Update data
 * @returns Promise<Trigger>
 */
export async function updateTrigger(triggerId: string, updateData: TriggerUpdate): Promise<Trigger> {
  const res = await fetch(`${API_BASE}/2.0/triggers/${encodeURIComponent(triggerId)}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'user_id': getUserId()
    },
    body: JSON.stringify(updateData)
  });
  
  if (!res.ok) await handleApiError(res);
  
  return res.json();
}

/**
 * Delete a trigger
 * @param triggerId - Trigger ID
 * @param deleteData - Delete data including image_id
 * @returns Promise<void>
 */
export async function deleteTrigger(triggerId: string, deleteData: TriggerDelete): Promise<void> {
  const res = await fetch(`${API_BASE}/2.0/triggers/${encodeURIComponent(triggerId)}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'user_id': getUserId()
    },
    body: JSON.stringify(deleteData)
  });
  
  if (!res.ok) await handleApiError(res);
}

/**
 * Create a custom trigger for an image
 * @param triggerData - Custom trigger data
 * @returns Promise<Trigger>
 */
export async function createCustomTrigger(triggerData: CustomTriggerRequest): Promise<Trigger> {
  const res = await fetch(`${API_BASE}/2.0/triggers`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'user_id': getUserId()
    },
    body: JSON.stringify(triggerData)
  });
  
  if (!res.ok) await handleApiError(res);
  
  return res.json();
}

// ==================== HIGH-LEVEL FUNCTIONS FOR MOBILE-TRIGGER-DETECTOR ====================

/**
 * Save complete analysis data including image, triggers, and metadata
 * This function orchestrates multiple API calls to save all analysis data
 * @param analysisData - Complete analysis data
 * @returns Promise<{ imageId: string; success: boolean }>
 */
export async function saveCompleteAnalysis(analysisData: AnalysisData & {
  imageUrl?: string;
  imageFile?: File;
}): Promise<{ imageId: string; success: boolean }> {
  try {
    // 1. Upload image if provided and it's a new image (not already processed)
    let imageId: string | null = null;
    
    if (analysisData.imageFile) {
      // Upload new image file
      const imageResponse = await uploadImage(analysisData.imageFile, analysisData.keyword);
      imageId = imageResponse.image_id;
    } else if (analysisData.imageUrl && analysisData.imageUrl.startsWith('data:')) {
      // Upload base64 image data
      const imageResponse = await uploadImage(analysisData.imageUrl, analysisData.keyword);
      imageId = imageResponse.image_id;
    } else if (analysisData.imageUrl && analysisData.imageUrl.startsWith('http')) {
      // Image is already uploaded and processed, don't re-upload
      // Extract image ID from URL or use a placeholder
      console.log('[saveCompleteAnalysis] Image already processed, skipping upload');
      imageId = 'already-processed';
    }
    
    // 2. If we have an image ID, save additional analysis metadata
    if (imageId && imageId !== 'already-processed') {
      // The triggers will be associated with the image automatically through the analysis
      // For now, we just ensure the keyword is properly set
      if (analysisData.keyword) {
        await updateImageKeyword(imageId, analysisData.keyword);
      }
    }
    
    return {
      imageId: imageId || 'no-image',
      success: true
    };
  } catch (error) {
    console.error('Failed to save complete analysis:', error);
    return {
      imageId: 'error',
      success: false
    };
  }
}

/**
 * Get analysis history for the current user with comprehensive data
 * @param limit - Number of items to fetch (optional, if not provided, gets all images)
 * @param includeUrls - Whether to include presigned URLs for images
 * @returns Promise<Image[]>
 */
export async function getAnalysisHistory(limit?: number, includeUrls: boolean = true): Promise<Image[]> {
  try {
    if (limit && limit > 0) {
      // Use paginated endpoint when limit is specified
      console.log(`[getAnalysisHistory] Fetching ${limit} images with pagination`)
      const response = await fetchImages({ limit, includeUrls });
      return response.images;
    } else {
      // Use /all endpoint to get all images when no limit specified
      console.log('[getAnalysisHistory] Fetching all images')
      const response = await fetchAllImages(includeUrls);
      return response.images;
    }
  } catch (error) {
    console.error('Failed to fetch analysis history:', error);
    return [];
  }
}

/**
 * Get comprehensive dashboard data including images and user info
 * @param limit - Number of images to fetch (optional, gets all if not specified)
 * @returns Promise<{ images: Image[], userInfo: UserInfo | null }>
 */
export async function getDashboardData(limit?: number): Promise<{ 
  images: Image[], 
  userInfo: UserInfo | null 
}> {
  try {
    console.log('[getDashboardData] Starting parallel data fetch with limit:', limit);
    
    // Fetch both images and user info in parallel
    const [imagesResult, userInfoResult] = await Promise.allSettled([
      getAnalysisHistory(limit, true), // Include URLs for dashboard display
      getUserInfo()
    ]);

    const images = imagesResult.status === 'fulfilled' ? imagesResult.value : [];
    const userInfo = userInfoResult.status === 'fulfilled' ? userInfoResult.value : null;

    console.log(`[getDashboardData] Results: ${images.length} images, userInfo: ${userInfo ? 'loaded' : 'failed'}`);

    if (imagesResult.status === 'rejected') {
      console.warn('[getDashboardData] Images fetch failed:', imagesResult.reason);
    }
    
    if (userInfoResult.status === 'rejected') {
      console.warn('[getDashboardData] User info fetch failed:', userInfoResult.reason);
    }

    // Log detailed information about the fetched images
    if (images.length > 0) {
      console.log('[getDashboardData] Images data summary:', {
        totalImages: images.length,
        imagesWithKeywords: images.filter(img => img.keyword).length,
        imagesWithAnalysis: images.filter(img => img.analysis).length,
        imagesWithTriggers: images.filter(img => img.triggers && img.triggers.length > 0).length,
        imagesWithSuggestions: images.filter(img => img.suggestions).length,
        imagesWithEmoIntensity: images.filter(img => img.emo_intensity_before || img.emo_intensity_after).length,
        imagesWithUserEvaluation: images.filter(img => img.relatability_after || img.helpfulness_after || img.learnability_after).length
      });

      // Log sample image data for debugging
      const sampleImage = images[0];
      console.log('[getDashboardData] Sample image data:', {
        image_id: sampleImage.image_id,
        keyword: sampleImage.keyword,
        upload_date: sampleImage.upload_date,
        hasUrl: !!sampleImage.url,
        analysisKeys: sampleImage.analysis ? Object.keys(sampleImage.analysis) : [],
        triggersCount: sampleImage.triggers ? sampleImage.triggers.length : 0,
        suggestionsKeys: sampleImage.suggestions ? Object.keys(sampleImage.suggestions) : [],
        emoIntensityBefore: sampleImage.emo_intensity_before,
        emoIntensityAfter: sampleImage.emo_intensity_after,
        userEvaluation: {
          relatability: sampleImage.relatability_after,
          helpfulness: sampleImage.helpfulness_after,
          learnability: sampleImage.learnability_after,
          comment: sampleImage.comment_after,
          bestSuggestion: sampleImage.best_suggestion
        }
      });
    } else {
      console.log('[getDashboardData] No images found');
    }

    return {
      images,
      userInfo
    };
  } catch (error) {
    console.error('[getDashboardData] Failed to fetch dashboard data:', error);
    return {
      images: [],
      userInfo: null
    };
  }
}

/**
 * Get enhanced analysis history with additional metadata
 * This function enriches the basic image data with analysis information
 * @param limit - Number of items to fetch
 * @returns Promise<Image[]> with enhanced analysis data
 */
export async function getEnhancedAnalysisHistory(limit?: number): Promise<Image[]> {
  try {
    const images = await getAnalysisHistory(limit, true);
    
    // Enhance each image with processed analysis data
    const enhancedImages = images.map(image => {
      // Ensure analysis data is properly structured
      if (image.analysis_text && !image.analysis) {
        try {
          // Try to parse analysis_text as JSON if analysis object is missing
          const parsedAnalysis = JSON.parse(image.analysis_text);
          image.analysis = parsedAnalysis;
          image.source = 'parsed';
        } catch (e) {
          // If parsing fails, mark as raw
          image.source = 'raw';
        }
      }

      // Ensure triggers have the required format for mobile-trigger-detector
      if (image.analysis?.triggers) {
        image.analysis.triggers = image.analysis.triggers.map(trigger => ({
          ...trigger,
          // Add backward compatibility fields
          id: trigger.trigger_id,
          confidence: 1.0, // Default confidence
          isAccepted: false, // Default acceptance status
          rating: null // Default rating
        }));
      }

      return image;
    });

    return enhancedImages;
  } catch (error) {
    console.error('Failed to fetch enhanced analysis history:', error);
    return [];
  }
}

/**
 * Delete analysis data (image and associated triggers)
 * @param imageId - Image ID to delete
 * @returns Promise<boolean>
 */
export async function deleteAnalysisData(imageId: string): Promise<boolean> {
  try {
    await deleteImage(imageId);
    return true;
  } catch (error) {
    console.error('Failed to delete analysis data:', error);
    return false;
  }
}

/**
 * Check if the API is available and user is properly configured
 * @returns Promise<boolean>
 */
export async function checkApiHealth(): Promise<boolean> {
  try {
    // Check if user ID is available (basic auth check)
    getUserId(); // This will throw if userId is not set
    
    // Instead of calling a protected endpoint, just return true if user ID is available
    // The actual API calls will handle authentication errors appropriately
    console.log('[checkApiHealth] User authentication available');
    return true;
  } catch (error) {
    if (error instanceof Error) {
      if (error.message.includes('Session key not found')) {
        console.error('API health check failed: User not authenticated', error);
        return false;
      }
    }
    console.error('API health check failed:', error);
    return false;
  }
}

// ==================== USER INFORMATION API v2.0 ====================

/**
 * Create or update user information
 * @param userInfo - User information to save
 * @returns Promise<UserInfoResponse>
 */
export async function createUserInfo(userInfo: UserInfoRequest): Promise<UserInfoResponse> {
  const res = await fetch(`${API_BASE}/2.0/users`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'user_id': getUserId()
    },
    body: JSON.stringify(userInfo)
  });
  
  if (!res.ok) await handleApiError(res);
  
  return res.json();
}

/**
 * Get user information
 * @returns Promise<UserInfo>
 */
export async function getUserInfo(): Promise<UserInfo> {
  const userId = getUserId();
  console.log('[getUserInfo] Making request with user_id:', userId);
  
  const res = await fetch(`${API_BASE}/2.0/users`, {
    method: 'GET',
    headers: {
      'user_id': userId
    }
  });
  
  console.log('[getUserInfo] Response status:', res.status);
  console.log('[getUserInfo] Response ok:', res.ok);
  
  if (!res.ok) {
    console.log('[getUserInfo] Response not ok, calling handleApiError');
    await handleApiError(res);
  }
  
  const data = await res.json();
  console.log('[getUserInfo] Response data:', data);
  return data;
}

/**
 * Create a new user with default settings
 * @returns Promise<NewUserResponse>
 */
export async function createNewUser(): Promise<NewUserResponse> {
  const res = await fetch(`${API_BASE}/2.0/users`, {
    method: 'POST'
  });
  
  if (!res.ok) await handleApiError(res);
  
  return res.json();
}

/**
 * User login with status activation
 * @param userId - User identifier
 * @returns Promise<UserLoginResponse>
 */
export async function userLogin(userId: string): Promise<UserLoginResponse> {
  console.log('[userLogin] Making request to:', `${API_BASE}/2.0/users/login`);
  console.log('[userLogin] User ID:', userId);
  
  const res = await fetch(`${API_BASE}/2.0/users/login`, {
    method: 'POST',
    headers: {
      'user_id': userId
    }
  });
  
  console.log('[userLogin] Response status:', res.status);
  console.log('[userLogin] Response ok:', res.ok);
  
  if (!res.ok) {
    console.log('[userLogin] Response not ok, calling handleApiError');
    await handleApiError(res);
  }
  
  const data = await res.json();
  console.log('[userLogin] Response data:', data);
  return data;
}

/**
 * Create or update user GAD-7 score
 * @param gad7Score - GAD-7 score (0-21)
 * @returns Promise<UserGad7Response>
 */
export async function createUserGad7(gad7Score: number): Promise<UserGad7Response> {
  const requestBody: UserGad7Request = {
    gad7_score: gad7Score
  };

  const res = await fetch(`${API_BASE}/2.0/users/gad7`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'user_id': getUserId()
    },
    body: JSON.stringify(requestBody)
  });
  
  if (!res.ok) await handleApiError(res);
  
  return res.json();
}

/**
 * Create or update user contact information
 * @param contactData - Contact information for interview and/or raffle participation
 * @returns Promise<UserContactResponse>
 */
export async function createUserContact(contactData: UserContactRequest): Promise<UserContactResponse> {
  const res = await fetch(`${API_BASE}/2.0/users/contact`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'user_id': getUserId()
    },
    body: JSON.stringify(contactData)
  });
  
  if (!res.ok) await handleApiError(res);
  
  return res.json();
}

// ==================== IMAGE INFO API v2.0 ====================

/**
 * Update emotion intensity before session
 * @param imageId - Image ID
 * @param emotionIntensity - Emotion intensity level (0-10)
 * @returns Promise<any>
 */
export async function updateImageInfoBefore(imageId: string, emotionIntensity: number): Promise<any> {
  const res = await fetch(`${API_BASE}/2.0/images/${encodeURIComponent(imageId)}/info_before`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'user_id': getUserId()
    },
    body: JSON.stringify({
      emo_intensity_before: emotionIntensity
    })
  });
  
  if (!res.ok) await handleApiError(res);
  
  return res.json();
}

/**
 * Update session feedback information
 * @param imageId - Image ID
 * @param feedback - Session feedback data
 * @returns Promise<any>
 */
export async function updateImageInfoAfter(imageId: string, feedback: {
  emo_intensity_after: number;
  relatability_after: number;
  helpfulness_after: number;
  learnability_after: number;
  comment_after?: string;
  best_suggestion?: string;
}): Promise<any> {
  const res = await fetch(`${API_BASE}/2.0/images/${encodeURIComponent(imageId)}/info_after`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'user_id': getUserId()
    },
    body: JSON.stringify(feedback)
  });
  
  if (!res.ok) await handleApiError(res);
  
  return res.json();
}

// ==================== AI SUGGESTION API ====================

/**
 * Generate AI-powered anxiety intervention suggestion for an image
 * @param imageId - Image ID
 * @param suggestionType - Type of suggestion to generate
 * @returns Promise<SuggestionResponse>
 */
export async function generateSuggestion(imageId: string, suggestionType: 'cognitive_reappraisal' | 'problem_focus_coping' | 'media_regulation'): Promise<SuggestionResponse> {
  const requestBody: SuggestionRequest = {
    type: suggestionType
  };

  const res = await fetch(`${API_BASE}/ai/images/${encodeURIComponent(imageId)}/suggestion`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'user_id': getUserId()
    },
    body: JSON.stringify(requestBody)
  });
  
  if (!res.ok) await handleApiError(res);
  
  return res.json();
}

/**
 * Report an AI-generated suggestion
 * @param imageId - Image ID
 * @param reportData - Suggestion report data
 * @returns Promise<SuggestionReportResponse>
 */
export async function reportSuggestion(imageId: string, reportData: SuggestionReportRequest): Promise<SuggestionReportResponse> {
  const res = await fetch(`${API_BASE}/ai/images/${encodeURIComponent(imageId)}/suggestion/report`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'user_id': getUserId()
    },
    body: JSON.stringify(reportData)
  });
  
  if (!res.ok) await handleApiError(res);
  
  return res.json();
}

/**
 * Rate an AI-generated suggestion
 * @param imageId - Image ID
 * @param ratingData - Suggestion rating data
 * @returns Promise<SuggestionRatingResponse>
 */
export async function rateSuggestion(imageId: string, ratingData: SuggestionRatingRequest): Promise<SuggestionRatingResponse> {
  const res = await fetch(`${API_BASE}/ai/images/${encodeURIComponent(imageId)}/suggestion/rating`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'user_id': getUserId()
    },
    body: JSON.stringify(ratingData)
  });
  
  if (!res.ok) await handleApiError(res);
  
  return res.json();
} 